// ═══════════════════════════════════════════════════════════════════════════════
// Recurrence Badge Component
// ═══════════════════════════════════════════════════════════════════════════════

import { useLanguage } from '../../contexts/LanguageContext';
import { RECURRENCE_TYPE, type RecurrenceType } from '@fdh/shared';

type RecurrenceBadgeProps = {
  recurrence: RecurrenceType;
  recurrenceDays?: number[];
  size?: 'sm' | 'md';
};

const RECURRENCE_STYLES: Record<RecurrenceType, string> = {
  [RECURRENCE_TYPE.NONE]: 'bg-gray-100 text-gray-600',
  [RECURRENCE_TYPE.DAILY]: 'bg-blue-100 text-blue-700',
  [RECURRENCE_TYPE.WEEKLY]: 'bg-purple-100 text-purple-700',
  [RECURRENCE_TYPE.BIWEEKLY]: 'bg-indigo-100 text-indigo-700',
  [RECURRENCE_TYPE.MONTHLY]: 'bg-teal-100 text-teal-700',
};

const SIZE_STYLES = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

const RECURRENCE_I18N_KEYS: Record<RecurrenceType, string> = {
  [RECURRENCE_TYPE.NONE]: 'task.recurrence.none',
  [RECURRENCE_TYPE.DAILY]: 'task.recurrence.daily',
  [RECURRENCE_TYPE.WEEKLY]: 'task.recurrence.weekly',
  [RECURRENCE_TYPE.BIWEEKLY]: 'task.recurrence.biweekly',
  [RECURRENCE_TYPE.MONTHLY]: 'task.recurrence.monthly',
};

const DAY_KEYS = [
  'task.day.sun',
  'task.day.mon',
  'task.day.tue',
  'task.day.wed',
  'task.day.thu',
  'task.day.fri',
  'task.day.sat',
];

export const RecurrenceBadge = ({
  recurrence,
  recurrenceDays = [],
  size = 'md',
}: RecurrenceBadgeProps) => {
  const { t } = useLanguage();

  const showDays =
    (recurrence === RECURRENCE_TYPE.WEEKLY ||
      recurrence === RECURRENCE_TYPE.BIWEEKLY) &&
    recurrenceDays.length > 0;

  const daysText = showDays
    ? recurrenceDays.map((d) => t(DAY_KEYS[d])).join(', ')
    : '';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ${RECURRENCE_STYLES[recurrence]} ${SIZE_STYLES[size]}`}
    >
      {t(RECURRENCE_I18N_KEYS[recurrence])}
      {daysText && (
        <span className="opacity-75">({daysText})</span>
      )}
    </span>
  );
};
